import { useEffect, useRef } from "react";

import { resolveBuffered } from "../../components/player-ui/helpers";
import { usePlayerStore } from "../../store/usePlayerStore";

const usePlayAudio = (audio?: HTMLAudioElement) => {
  const { useStoreState } = usePlayerStore();
  const analyticsEvents = useStoreState.analyticsEvents();
  const snowplowEvents = useStoreState.snowplowEvents();
  const externalEvents = useStoreState.externalEvents();
  const isPlayerSetupDone = useStoreState.isPlayerSetupDone();
  const setBuffered = useStoreState.setBuffered();
  const setCurrentTime = useStoreState.setCurrentTime();
  const setIsEnded = useStoreState.setIsEnded();
  const setError = useStoreState.setError();
  const setIsPaused = useStoreState.setIsPaused();
  const setPlaybackRate = useStoreState.setPlaybackRate();
  const setIsSeeking = useStoreState.setIsSeeking();

  const isBuffering = useRef(false);

  useEffect(() => {
    if (audio && isPlayerSetupDone && externalEvents) {
      const events = [
        {
          event: "ended",
          fn: () => {
            audio.currentTime = 0;
            setIsEnded(true);
            setIsPaused(true);
            analyticsEvents?.onFinish();
            snowplowEvents?.onFinish();
          }
        },
        {
          event: "play",
          fn: () => {
            setIsPaused(false);
            setIsEnded(false);
            setError(undefined);
            analyticsEvents?.onPlay();
            snowplowEvents?.onPlay();
          }
        },
        {
          event: "playing",
          fn: () => {
            externalEvents.dispatch("playing");
            if (isBuffering.current) {
              snowplowEvents?.onBufferEnd();
              isBuffering.current = false;
            }
          }
        },
        {
          event: "pause",
          fn: () => {
            setIsPaused(true);
            analyticsEvents?.onPause();
            snowplowEvents?.onPause();
          }
        },
        {
          event: "progress",
          fn: (event: Event) => {
            const audioElement = event.target as HTMLAudioElement;
            setBuffered(
              resolveBuffered({
                buffered: audioElement.buffered,
                currentTime: audioElement.currentTime
              })
            );
          }
        },
        {
          event: "ratechange",
          fn: event => {
            const rate = (event.target as HTMLAudioElement).playbackRate;
            setPlaybackRate(rate);
            snowplowEvents?.onPlaybackRateChange(rate);
          }
        },
        {
          event: "seeked",
          fn: () => {
            setIsSeeking(false);
            analyticsEvents?.onSeekEnd();
            snowplowEvents?.onSeekEnd();
          }
        },
        {
          event: "seeking",
          fn: () => {
            setIsSeeking(true);
            analyticsEvents?.onSeekStart();
            snowplowEvents?.onSeekStart();
          }
        },
        {
          event: "timeupdate",
          fn: (event: Event) => {
            setCurrentTime((event.target as HTMLAudioElement).currentTime);
            analyticsEvents?.onTimeUpdate(
              (event.target as HTMLAudioElement).currentTime
            );
            snowplowEvents?.onTimeUpdate(
              (event.target as HTMLAudioElement).currentTime
            );
            externalEvents.dispatch("onTimeUpdate");
          }
        },
        {
          event: "waiting",
          fn: () => {
            isBuffering.current = true;
            snowplowEvents?.onBufferStart();
          }
        },
        {
          event: "volumechange",
          fn: (event: Event) => {
            const target = event.target as HTMLAudioElement;
            snowplowEvents?.onVolumeChange(
              target.muted ? 0 : target.volume * 100
            );
          }
        }
      ];

      events.forEach(({ event, fn }) => audio.addEventListener(event, fn));

      return () => {
        events.forEach(({ event, fn }) => audio.removeEventListener(event, fn));
      };
    }
  }, [
    audio,
    externalEvents,
    isPlayerSetupDone,
    setCurrentTime,
    setIsEnded,
    setError,
    setIsPaused,
    setIsSeeking,
    setBuffered,
    setPlaybackRate,
    analyticsEvents,
    snowplowEvents
  ]);
};

export default usePlayAudio;
